import React, { useState, type FormEvent } from 'react';
import { Button } from '../shared';

export interface SetupScreenProps {
  onSubmit: (password: string) => Promise<void>;
  onSkip: () => void;
}

/**
 * Password creation form shown when the lock is enabled but not yet configured.
 * There is no password recovery — warn the user before they commit.
 */
export function SetupScreen({ onSubmit, onSkip }: SetupScreenProps): React.ReactElement {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const passwordsMatch = password.length > 0 && password === confirm;
  const canSubmit = passwordsMatch && password.length >= 4 && !busy;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set password.');
      setBusy(false);
    }
  };

  return (
    <div className="applock-screen" role="main">
      <form className="applock-card" onSubmit={handleSubmit}>
        <div className="applock-card__icon" aria-hidden="true">
          &#128272;
        </div>
        <h1 className="applock-card__title">Create a password</h1>
        <p className="applock-card__text">
          This password unlocks the app. Choose something memorable — you&rsquo;ll need it every
          time you return.
        </p>

        <div className="input-group">
          <label htmlFor="applock-setup-password" className="input-group__label">
            Password
          </label>
          <input
            id="applock-setup-password"
            className="input-group__input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="input-group">
          <label htmlFor="applock-setup-confirm" className="input-group__label">
            Confirm password
          </label>
          <input
            id="applock-setup-confirm"
            className="input-group__input"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        <p className="applock-card__warning" role="note">
          <strong>No password recovery.</strong> If you forget your password, the app must be reset
          and your data will be lost.
        </p>

        {!passwordsMatch && confirm.length > 0 && (
          <p className="applock-card__error" role="alert">
            Passwords do not match.
          </p>
        )}
        {password.length > 0 && password.length < 4 && (
          <p className="applock-card__error" role="alert">
            Password must be at least 4 characters.
          </p>
        )}
        {error && (
          <p className="applock-card__error" role="alert">
            {error}
          </p>
        )}

        <div className="applock-card__actions">
          <Button variant="primary" size="md" type="submit" disabled={!canSubmit} loading={busy}>
            Create password
          </Button>
          <Button
            variant="ghost"
            size="md"
            type="button"
            disabled={busy}
            onClick={onSkip}
          >
            Skip
          </Button>
        </div>
      </form>
    </div>
  );
}
